import { fetchExperienceList } from 'actions/ExperienceActions'
import { EmptyExperienceList } from 'constants/experience'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { take, without } from 'lib/array/arrayUtils'
import { getExperienceListKey } from 'lib/experiences/experienceUtils'
import { useEffect, useMemo } from 'react'

const EMPTY_LIST = { ...EmptyExperienceList, fetching: false }

interface Params {
  latitude?: number
  longitude?: number
  categoryCodes?: Array<number>
  distance?: string
  from?: string
  to?: string
  postPurchaseOnly?: boolean
  offerId?: string
  limit?: number
  excludeIds?: Array<string>
  experienceIdsNotIn?: Array<string>
  isLoyaltyEligible?: boolean
  showUnlisted?: boolean
  experienceIds?: Array<string>
  includeFullCopy?: boolean
}

function useExperienceList(params: Params): [App.ApiCallState<{ ids: Array<string> }> & { listKey: string }, Array<App.ExperienceOffer>] {
  const {
    distance,
    limit = 8,
    from,
    to,
    postPurchaseOnly,
    categoryCodes,
    latitude,
    longitude,
    offerId,
    excludeIds = [],
    experienceIdsNotIn,
    isLoyaltyEligible,
    showUnlisted,
    experienceIds,
    includeFullCopy,
  } = params

  const dispatch = useAppDispatch()
  const locale = useAppSelector(state => state.geo.locale)

  const requiresCoordinates = !experienceIds?.length
  const fullCopyLocale = includeFullCopy ? locale : undefined

  const distanceRange = useMemo(() => distance ? ['0KM', distance] : undefined, [distance])

  const listKey = useMemo(() => {
    return getExperienceListKey({ latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible, showUnlisted, experienceIds, experienceIdsNotIn, includeFullCopy, locale: fullCopyLocale })
  }, [latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible, showUnlisted, experienceIds, experienceIdsNotIn, includeFullCopy, fullCopyLocale])

  const experienceList = useAppSelector((state) => state.experience.experienceLists[listKey] ?? EMPTY_LIST)
  const experiences = useAppSelector((state) => state.experience.experiences)

  useEffect(() => {
    if (!requiresCoordinates || (latitude && longitude)) {
      dispatch(fetchExperienceList({ latitude, longitude, distanceRange, categoryCodes, from, to, postPurchaseOnly, offerId, isLoyaltyEligible, showUnlisted, experienceIds, experienceIdsNotIn, includeFullCopy }))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCodes, latitude, longitude, distanceRange, from, to, postPurchaseOnly, isLoyaltyEligible, showUnlisted, experienceIds, experienceIdsNotIn, includeFullCopy, fullCopyLocale])

  const list = useMemo((): App.ApiCallState<{ ids: Array<string> }> & { listKey: string } => {
    if (excludeIds.length === 0) {
      return { ...experienceList, listKey }
    }

    return {
      ...experienceList,
      ids: without(experienceList.ids, ...excludeIds),
      listKey,
    }
  }, [experienceList, excludeIds, listKey])

  const experienceItems = useMemo(() => {
    return take(list.ids, limit).map(id => experiences[id]).filter(Boolean)
  }, [experiences, list, limit])

  return [
    list,
    experienceItems,
  ]
}

export default useExperienceList
